<template>
    <AppForm>
        <FormRow class="participant-title">
            <span>{{ name ? name : $t("Data space participant") }}</span>
        </FormRow>
        <FormRow>
            <DataTable>
                <TableRow>
                    <TableCell class="bold">{{ $t("ID") }}</TableCell>
                    <TableCell>
                        <NavAction
                            v-if="(participantId + '').startsWith('did:')"
                            @click="viewDidDocument"
                            :title="$t('DID Document')"
                            :text="participantId || '-'"
                        />
                        <span v-else>{{ participantId || "-" }}</span>
                    </TableCell>
                </TableRow>
                <TableRow v-if="url">
                    <TableCell class="bold">{{ $t("Connector URL") }}</TableCell>
                    <TableCell v-if="loading"><LoadingState :text="$t('Loading...')" /></TableCell>
                    <TableCell v-else>{{ url }}</TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </DataTable>
        </FormRow>
        <FormRow class="view-certificate-section">
            <ActionButton
                v-if="firstAttestationIndex !== null"
                :style-type="'neutral-filled'"
                :title="$t('Attestation')"
                :text="$t('View certificate')"
                @click="viewAttestation(firstAttestationIndex)"
            />
        </FormRow>
        <FormRow v-if="attributes.length > 0">
            <FormField
                :label="$t('Attributes')"
                :description="$t('The identity claims the attestator signed, such as the legal name or the country of the participant')"
            >
                <DataTable :column-headers="[$t('Attribute'), $t('Value')]">
                    <TableRow v-for="(a, i) in attributes" :key="i">
                        <TableCell>{{ a.name }}</TableCell>
                        <TableCell>{{ a.value }}</TableCell>
                    </TableRow>
                </DataTable>
            </FormField>
        </FormRow>
    </AppForm>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { getUniqueStringId } from "@/utils/unique-id";
import { Timeouts } from "@/utils/timeout";
import { ApiDataSpaceParticipants } from "@/api/api-group-data-space-participants";
import { DataSpaceKnownParticipantAttestation, DataSpaceKnownParticipantAttribute } from "@/api/definitions";
import { Request } from "@asanrom/request-browser";
import { AuthController } from "@/control/auth";
import DataTable from "@/components/tables/data-table/DataTable.vue";
import TableRow from "@/components/tables/data-table/TableRow.vue";
import TableCell from "@/components/tables/data-table/TableCell.vue";
import NavAction from "@/components/navigation/NavAction.vue";
import FormField from "@/components/forms/form/fields/FormField.vue";
import ActionButton from "../buttons/ActionButton.vue";
import AppForm from "../forms/form/AppForm.vue";
import FormRow from "../forms/form/FormRow.vue";
import LoadingState from "../loading/LoadingState.vue";

type DataSpaceKnownParticipantAttributeExtended = DataSpaceKnownParticipantAttribute & {
    attestation: number;
};

function mapAttributes(
    attributes: DataSpaceKnownParticipantAttribute[],
    attestations: DataSpaceKnownParticipantAttestation[],
): DataSpaceKnownParticipantAttributeExtended[] {
    const attMap = new Map<string, number>();
    attestations.forEach((a, i) => {
        a.attributes.forEach((atr) => {
            attMap.set(atr.name, i);
        });
    });

    return attributes.map((a) => {
        let attestation = -1;

        if (attMap.has(a.name)) {
            attestation = attMap.get(a.name);
        }

        return {
            name: a.name,
            value: a.value,
            attestation: attestation,
        };
    });
}

export default defineComponent({
    components: {
        DataTable,
        TableRow,
        TableCell,
        NavAction,
        ActionButton,
        AppForm,
        FormRow,
        LoadingState,
        FormField,
    },
    name: "DataSpaceParticipantModalContent",
    emits: ["update-shared-data", "go-to-step"],
    props: {
        display: Boolean,
        participantId: String,
        allowFilter: Boolean,
    },
    setup() {
        return {
            loadRequestId: getUniqueStringId(),
            updateRequestId: getUniqueStringId(),
        };
    },
    data() {
        return {
            loading: true,

            name: "",
            url: "",

            canUpdate: AuthController.hasPermission("participants.admin"),
            attributes: [] as DataSpaceKnownParticipantAttributeExtended[],
            attestations: [] as DataSpaceKnownParticipantAttestation[],

            selectedAttestation: null as DataSpaceKnownParticipantAttestation,
        };
    },
    computed: {
        firstAttestationIndex(): number | null {
            return this.attributes.find((a) => a.attestation >= 0)?.attestation ?? null;
        },
    },
    methods: {
        load: function () {
            Timeouts.Abort(this.loadRequestId);

            this.loading = true;
            this.attributes = [];

            Request.Pending(this.loadRequestId, ApiDataSpaceParticipants.GetKnownParticipant(this.participantId))
                .onSuccess((res) => {
                    this.loading = false;

                    this.name = res.name;
                    this.url = res.url;
                    this.attributes = mapAttributes(res.attributes, res.attestations);
                    this.attestations = res.attestations;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        notFound: () => {
                            this.name = "";
                            this.url = "";
                            this.attributes = [];
                            this.loading = false;
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    Timeouts.Set(this.loadRequestId, 1500, this.load.bind(this));
                });
        },

        viewAttestation: function (i: number) {
            const att = this.attestations[i];

            if (!att) {
                return;
            }

            this.selectedAttestation = att;

            this.$emit("update-shared-data", {
                attestation: this.selectedAttestation,
                subject: this.participantId,
                subjectName: this.name,
            });

            this.$emit("go-to-step", "attestation-view-modal");
        },

        viewDidDocument: function () {
            this.$emit("update-shared-data", {
                did: this.participantId,
            });

            this.$emit("go-to-step", "document-did-modal");
        },
    },
    mounted: function () {
        this.$listenOnAppEvent("auth-status-changed", () => {
            this.canUpdate = AuthController.hasPermission("participants.admin");
        });

        this.$autoFocus();
        this.load();
    },
    beforeUnmount: function () {
        Timeouts.Abort(this.loadRequestId);
        Request.Abort(this.loadRequestId);
        Request.Abort(this.updateRequestId);
    },
    watch: {
        participantId: function () {
            this.load();
        },
    },
});
</script>

<style scoped>
.view-certificate-section {
    display: flex;
    justify-content: flex-end;
}
.participant-title {
    font-size: 1.25rem;
    font-weight: bold;
}
</style>
